import {IssueClosedIcon, IssueOpenedIcon} from '@primer/octicons-react'
import {load} from 'js-yaml'
import {useMemo} from 'react'

import {useContentPreview} from '../../../components/ContentPreview/ContentPreviewContext'
import {type BlockedByDependencies, IssueTree} from '../../../components/ContentPreview/create-issue-preview/IssueTree'
import {
  toTreeMap,
  type TreeNode,
  visit,
} from '../../../components/ContentPreview/create-issue-preview/use-draft-issue-tree-map'
import {useContentPreviewBlockContext} from '../../ContentPreviewBlockContext'
import {useSyncState} from '../../hooks/use-sync-state'
import styles from '../draft-issue-tree/DraftIssueTreeBlock.module.css'

export interface IssueTreeItem {
  tag?: string
  parentTag?: string
  title?: string
  repository?: string
  number?: number
  state?: string
  url?: string
  blockedByTags?: string[]
  isBlockingTags?: string[]
}

export const IssueTreeItem = {
  fromUnknown: (data: unknown): IssueTreeItem | null => {
    if (typeof data !== 'object' || data === null) return null

    const item: IssueTreeItem = {}

    if ('tag' in data && typeof data.tag === 'string') item.tag = data.tag
    if ('parentTag' in data && typeof data.parentTag === 'string') item.parentTag = data.parentTag
    if ('title' in data && typeof data.title === 'string') {
      item.title = data.title
    } else if ('name' in data && typeof data.name === 'string') {
      item.title = data.name
    }
    if ('repository' in data && typeof data.repository === 'string') item.repository = data.repository
    if ('number' in data && typeof data.number === 'number') item.number = data.number
    if ('state' in data && typeof data.state === 'string') item.state = data.state
    if ('url' in data && typeof data.url === 'string') item.url = data.url
    if ('blockedByTags' in data && Array.isArray(data.blockedByTags)) {
      item.blockedByTags = data.blockedByTags.filter(el => typeof el === 'string')
    }
    if ('isBlockingTags' in data && Array.isArray(data.isBlockingTags)) {
      item.isBlockingTags = data.isBlockingTags.filter(el => typeof el === 'string')
    }

    return item
  },
}

function parseList(yaml: string) {
  try {
    const result = load(yaml)

    if (typeof result === 'object' && result !== null && 'data' in result && Array.isArray(result.data)) {
      return result.data
        .map(IssueTreeItem.fromUnknown)
        .filter(item => !!item)
        .filter(item => !!item.tag)
    }

    return null
  } catch {
    return null
  }
}

export interface IssueTreeBlockProps {
  data: string
}

export function IssueTreeBlock({data}: IssueTreeBlockProps) {
  const items = useSyncState(data, parseList, [])

  const {messageId} = useContentPreviewBlockContext()
  const {updateItem, openItem, openPreviewPane} = useContentPreview()

  const rootNodes = useMemo(() => {
    const tree = toTreeMap(
      items,
      item => item.tag!,
      item => item.parentTag,
      item => item.blockedByTags,
      item => item.isBlockingTags,
    )

    return Array.from(tree.values()).filter(node => !node.parent)
  }, [items])

  function onClick(item: IssueTreeItem, event?: React.MouseEvent) {
    if (!item.url) return

    const [owner, repo] = item.repository?.split('/') ?? []
    if (!owner || !repo || !item.number) return

    const id = `issue:${owner}/${repo}/${item.number}` as const
    updateItem({
      messageId,
      name: item.title ?? '',
      number: item.number,
      owner,
      repo,
      id,
      href: item.url,
      type: 'issue',
    })
    openItem(id)
    openPreviewPane(event?.currentTarget as HTMLElement)
  }

  const blockedByDependencies = (node: TreeNode<IssueTreeItem>): BlockedByDependencies[] | undefined => {
    return node.blockedBy.map(b => {
      return {
        title: b.item.title ?? '',
        href: b.item.url,
        onClick: e => {
          if (e.metaKey || e.ctrlKey) return
          e.preventDefault()

          onClick(b.item, e)
        },
      }
    })
  }

  function renderNode(node: TreeNode<IssueTreeItem>) {
    return (
      <IssueTree.Item
        key={node.item.tag}
        title={node.item.title ?? ''}
        leadingVisual={<LeadingVisual item={node.item} />}
        trailingBadge={<IssueLocation node={node} />}
        href={node.item.url}
        onClick={e => {
          if (e.metaKey || e.ctrlKey) return
          e.preventDefault()

          return onClick(node.item, e)
        }}
        blockedBy={blockedByDependencies(node)}
      >
        {node.children.map(renderNode)}
      </IssueTree.Item>
    )
  }

  return (
    <div className={styles.container}>
      {rootNodes.map(node => {
        let subIssueCount = -1 // start at -1 to not count the current node itself
        visit(node, () => subIssueCount++)

        return (
          <IssueTree key={node.item.tag} subIssueCount={subIssueCount}>
            <IssueTree.RootItem
              leadingVisual={<LeadingVisual item={node.item} />}
              title={node.item.title ?? ''}
              trailingBadge={<IssueLocation node={node} />}
              href={node.item.url}
              onClick={e => {
                if (e.metaKey || e.ctrlKey) return
                e.preventDefault()

                return onClick(node.item, e)
              }}
              blockedBy={blockedByDependencies(node)}
            />
            {node.children.map(renderNode)}
          </IssueTree>
        )
      })}
    </div>
  )
}

function LeadingVisual({item}: {item: IssueTreeItem}) {
  if (item.state === 'open') {
    return <IssueOpenedIcon className="fgColor-open" />
  }
  if (item.state === 'closed') {
    return <IssueClosedIcon className="fgColor-done" />
  }
}

function IssueLocation({node}: {node: TreeNode<IssueTreeItem>}) {
  if (!node.item.repository || !node.item.number) return null

  // If issue is in the same repository as its parent, just show the number
  if (node.item.repository === node.parent?.item.repository) {
    return <span className="fgColor-muted no-wrap">#{node.item.number}</span>
  }

  return (
    <span className="fgColor-muted no-wrap">
      {node.item.repository}#{node.item.number}
    </span>
  )
}
